import axios from 'axios';
import { loadLotterySource } from './lotterySources';

const ZODIACS = ['鼠', '牛', '虎', '兔', '龙', '蛇', '马', '羊', '猴', '鸡', '狗', '猪'];
const simplify = (value) => String(value).replace('雞', '鸡').replace('龍', '龙').replace('馬', '马').replace('豬', '猪');
export function drawTimestamp(value) {
  if (typeof value === 'number') return value;
  if (!value) return NaN;
  // Upstreams publish Hong Kong/Macau wall time. Never use the viewer's timezone.
  const text = String(value).trim();
  return Date.parse(/^\d{4}-\d{2}-\d{2}(?: \d{2}:\d{2}:\d{2})?$/.test(text)
    ? `${text.replace(' ', 'T')}${text.length === 10 ? 'T00:00:00' : ''}+08:00` : text);
}
export function normalizeDraw(raw, year) {
  if (!raw) return null;
  const code = raw.preDrawCode ?? raw.openCode;
  if (typeof code !== 'string') return null;
  const numbers = code.split(',').map(x => x.trim() === '' ? NaN : Number(x));
  if (numbers.length !== 7 || new Set(numbers).size !== 7 || numbers.some(n => !Number.isInteger(n) || n < 1 || n > 49)) return null;
  let issue = String(raw.preDrawIssue ?? raw.expect ?? raw.issue ?? '');
  if (!/^\d+$/.test(issue)) return null;
  if (issue.length < 4) issue = `${year}${issue.padStart(3, '0')}`;
  const date = raw.preDrawTime ?? raw.preDrawDate ?? raw.openTime;
  const timestamp = drawTimestamp(date);
  if (!Number.isFinite(timestamp)) return null;
  const values = raw.chineseZodiac ?? raw.czAndFe ?? (typeof raw.zodiac === 'string' ? raw.zodiac.split(',') : []);
  const zodiacs = numbers.map((_, i) => typeof values[i] === 'number' ? ZODIACS[values[i] - 1] || null : (ZODIACS.includes(simplify(values[i])) ? simplify(values[i]) : null));
  const elementNames = ['金', '木', '水', '火', '土'];
  const rawElements = Array.isArray(raw.fiveElements) ? raw.fiveElements : [];
  const elements = numbers.map((_, i) => {
    const value = rawElements[i];
    return typeof value === 'number' ? elementNames[value - 1] || null : elementNames.includes(value) ? value : null;
  });
  return { issue, date, timestamp, numbers, zodiacs, elements };
}
export function normalizeHistory(rows, year) {
  if (!Array.isArray(rows)) throw new Error('历史数据格式不正确');
  const records = rows.map(row => normalizeDraw(row, year));
  if (records.some(row => !row)) throw new Error('历史记录含有无效号码或日期，已暂停统计');
  const unique = new Map();
  for (const row of records) {
    const previous = unique.get(row.issue);
    if (previous && previous.numbers.join() !== row.numbers.join()) throw new Error('同一期号出现冲突数据，已暂停统计');
    unique.set(row.issue, row);
  }
  return [...unique.values()].sort((a, b) => b.timestamp - a.timestamp || b.issue.localeCompare(a.issue, undefined, { numeric: true }));
}
export async function fetchLottery(game, year, signal) {
  if (!['hk', 'macau'].includes(game) || !Number.isInteger(year) || year < 2025 || year > new Date().getFullYear()) {
    throw new Error('彩种或年份不正确');
  }
  const request = async url => {
    const response = await axios.get(url, { signal, timeout: 18000, withCredentials: false });
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('开奖数据源返回格式异常，请稍后重试');
    }
    return response.data;
  };
  const data = await loadLotterySource(request, game, year);
  if (signal?.aborted) throw new axios.CanceledError();
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('开奖服务暂未正确连接，请稍后重试');
  }
  const latest = normalizeDraw(data.latest, new Date().getFullYear());
  if (!latest) throw new Error('最新开奖数据格式不正确');
  const history = normalizeHistory(data.history, year);
  // A provider may update its history a moment after its latest endpoint.
  if (data.historyAvailable !== false && Number(latest.issue.slice(0, 4)) === year) {
    const match = history.find(row => row.issue === latest.issue);
    if (match && match.numbers.join() !== latest.numbers.join()) throw new Error('最新开奖与历史数据冲突');
    if (!match) history.unshift(latest);
  }
  return { ...data, latest, history, nextTime: drawTimestamp(data.latest.drawTime), nextIssue: data.latest.drawIssue };
}
